<template>
  <div class="profile">
    <h2 class="dashboard__tab-title">My Profile</h2>
    <div v-if="!isComponentLoading" class="profile__items-section">
      <div class="profile__items-wrapper">
        <BusinessLicenseBanner
          v-if="showBanner"
          :license="license"
          :businessAccountData="businessAccountData"
        />
        <ProfilePasswordUpdate
          @itemActive="handleItemActive"
          :canEdit="canEdit('password')"
          ref="password"
        />
        <PhoneNumber
          :currentPhoneNumber="phone"
          @itemActive="handleItemActive"
          :canEdit="canEdit('phone')"
          ref="phone"
        />
        <ManagePayment
          @itemActive="handleItemActive"
          :canEdit="canEdit('payment')"
          :paymentInfo="paymentInfo"
          ref="payment"
        />
        <MFAMethod
          @itemActive="handleItemActive"
          :userInfo="userInfo"
          :canManageMFA="canManageMFA"
          :canEdit="canEdit('mfa')"
          ref="mfa"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import BusinessLicenseBanner from '@/components/user-dashboard/profile/BusinessLicenseBanner.vue'
import PhoneNumber from '@/components/user-dashboard/profile/PhoneNumber.vue'
import MFAMethod from '@/components/user-dashboard/profile/MFAMethod.vue'
import ManagePayment from '@/components/user-dashboard/profile/ManagePayment.vue'
import ProfilePasswordUpdate from '@/components/user-dashboard/profile/ProfilePasswordUpdate.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'Profile',
  components: {
    ProfilePasswordUpdate,
    BusinessLicenseBanner,
    PhoneNumber,
    MFAMethod,
    ManagePayment,
  },
  data() {
    return {
      itemActive: '',
    }
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  methods: {
    handleItemActive(item) {
      this.itemActive = item
    },
    canEdit(type) {
      return type === this.itemActive
    },
  },
  watch: {
    itemActive() {
      this.$nextTick(() => {
        if (this.itemActive && this.canEdit(this.itemActive)) {
          setTimeout(() => {
            if (!this.itemActive || this.itemActive === 'password') return
            this.$scrollTo(`#${this.itemActive}`, 1000, {
              container: 'body',
              offset: -150,
            })
          }, 1000)
        }
      })
    },
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      profile: state => state.auth.userProfile,
      currentAvatar: state => state.auth.currentAvatar,
      paymentInfo: state => state.subscriptions.paymentInfo,
      isPageLoading: state => state.app.isPageLoading,
      isComponentLoading: state => state.app.isComponentLoading,
      businessUser: state => state.business.businessUser,
    }),
    ...mapGetters({
      canManageMFA: 'auth/canManageMFA',
      hasBusinessUserAccount: 'business/isBusinessUser',
      isFreeTrialUser: 'auth/isFreeTrialUser',
      canViewBillingInfo: 'subscriptions/canViewBillingInfo',
    }),
    email() {
      const { email } = this.userInfo
      return email
    },
    phone() {
      const { phoneNumber: phone } = this.userInfo
      return phone || 'None'
    },
    paymentMethod() {
      const {
        paymentInfo: { payment_method },
      } = this
      return payment_method
    },
    isAPayPalCustomer() {
      return (
        this.paymentMethod &&
        this.paymentMethod === CONSTANTS.SUBSCRIPTIONS.PAYPAL_PAYMENT_TYPE
      )
    },
    last4OfCC() {
      const {
        paymentInfo: { last4_of_cc },
      } = this
      return last4_of_cc
    },
    userInfo() {
      const {
        email,
        preferred_phone_number: phoneNumber,
        mfa_method: mfaMethod,
      } = this.profile
      return { email, phoneNumber, mfaMethod }
    },
    license() {
      return this.businessUser?.user?.license
    },
    showBanner() {
      return this.hasBusinessUserAccount
    },
    businessAccountData() {
      return {
        accountName: this.businessUser?.account?.name,
        accountLogo: this.businessUser?.account?.logo_url,
      }
    },
  },
  async created() {
    this.$store.dispatch('app/setIsComponentLoading', false)
    await this.$store.dispatch('auth/getOnboardingSurvey')
  },
}
</script>

<style lang="scss" scoped>
.profile {
  $this: &;
  &::v-deep input::placeholder {
    color: var(--gray-nine);
  }
  &__items-section {
    max-width: 1200px;
  }
  &::v-deep &__item {
    cursor: pointer;
    background: var(--glass-mid-gray);
    padding: 20px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    height: 92px;
    &:not(.nohover):hover {
      border: 1px solid var(--mulled-wine-50);
      background: var(--glass-extra-light);
      box-sizing: border-box;
      .dashboard__action-icon {
        border-color: var(--white);
      }
    }
    &--opened {
      &:hover {
        border: none;
      }
    }
    &-select {
      appearance: none;
      position: relative;
      &-icon {
        content: '';
        font-size: 1rem;
        top: 14px;
        right: 13px;
        position: absolute;
        color: var(--light-blue-gray);
        pointer-events: none;
      }
    }
    &-container {
      margin-bottom: 10px;
      background: var(--glass-mid-gray);
      border-radius: 10px;
    }
    &-data-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-left: 20px;
      font-size: rem(16);
      line-height: 23px;
    }
    &-lead {
      margin: 0;
      color: var(--white);
    }
    &-data {
      margin: 0;
      color: var(--light-blue-gray);
      line-height: 24px;
    }
    &-edit-wrapper {
      padding: 20px 20px 20px 20px;
      @include mobile {
        display: flex;
        flex-direction: column;
        width: 100%;
      }
    }
    &-edit {
      &-btn-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        margin-top: 40px;
        width: 100%;
      }
      &-btn {
        width: 100%;
        margin-bottom: 20px;
        height: 36px;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
    &-btn-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      margin-top: 50px;
      @include mobile {
        display: flex;
        flex-direction: row-reverse;
        justify-content: flex-start;
        align-items: flex-end;
        button {
          width: fit-content;
          margin: 0 0 0 20px;
        }
      }
    }
    &-bottom-wrapper {
      @include mobile {
        display: flex;
        .password-verifier {
          height: fit-content;
          width: 60%;
          padding: 10px 0 0 0;
        }
      }
    }
    &-action-wrapper {
      display: flex;
      justify-content: center;
    }
    &-responsive {
      display: flex;
      width: 100%;
      flex-direction: column;
      margin-top: 5px;
      margin-bottom: 5px;

      @include mobile {
        flex-direction: row;
        margin-top: unset;
        margin-bottom: unset;
      }
    }
  }
  &::v-deep &__icon {
    &-wrapper {
      position: relative;
      border-radius: 50%;
      background: var(--glass-mid-gray);
      color: var(--chetwode-blue);
      border: 1px solid var(--chetwode-blue-50);
      width: 52px;
      min-width: 52px;
      height: 52px;
      display: flex;
      justify-content: center;
      align-items: center;
      &::before {
        position: absolute;
        content: '';
        width: 100%;
        height: 100%;
        background: var(--black);
        z-index: -1;
        border-radius: 50%;
      }

      &--white {
        background: white;
      }
    }
    &--img {
      width: inherit;
      border-radius: 100px;
    }
    font-size: rem(19);
  }
  &::v-deep &__banner-btn {
    width: 80%;
    height: 40px;
    margin-left: 15px;

    font-size: 14px;
    line-height: 20px;
    font-weight: 450;

    @include mobile {
      margin-left: auto;
      width: unset;
    }

    &--disabled {
      pointer-events: none;
      opacity: 0.65;
    }
  }
}
</style>
